import React, { useMemo } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";

/**
 * @typedef {object} _TooltipOptions
 * @property {string} [id] Unique ID for the tooltip. If this isn't provided, a random UUID will be generated.
 * @property {import("react-tooltip").PlacesType} [place] Where the tooltip should appear. Defaults to "top".
 * @property {import("react-tooltip").VariantType} [variant] The variant of the tooltip. Defaults to "dark".
 * @property {boolean} [disableFloat] If true, the tooltip will not float. By default, the tooltip will float, which means the tooltip will follow the mouse posiiton.
 * @property {React.ReactNode} content Content for the tooltip.
 * @property {keyof import("react").ReactHTML} [childrenWrapperElement] The element to wrap the children in. Defaults to "span".
 * @property {React.ReactNode} children The tooltip will be displayed when hovering over this element.
 */

/**
 * @typedef {_TooltipOptions & Omit<import("react-tooltip").ITooltip, "float" | "children">} TooltipProps
 */

/**
 * Wraps the react-tooltip component to make it easier to use.
 * @param {TooltipProps} props
 */
function Tooltip({
    id,
    content,
    place = "top",
    variant = "dark",
    disableFloat,
    childrenWrapperElement = "span",
    children,
    ...restProps
}) {
    const tooltipId = useMemo(() => {
        if (!id) {
            console.warn("No tooltip ID provided. A random UUID will be used.");
            return crypto.randomUUID();
        }

        return id;
    }, [id]);

    return (
        <>
            {React.createElement(
                childrenWrapperElement,
                {
                    "data-tooltip-id": tooltipId,
                },
                children,
            )}

            <ReactTooltip id={tooltipId} place={place} variant={variant} float={!disableFloat} {...restProps}>
                {content}
            </ReactTooltip>
        </>
    );
}

export default Tooltip;
